import { Box, Stack, Typography } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useGetSiteReviewPeriodsQuery } from "src/store/api/generatedApi";
import { EXCEPTIONAL_STATUS_STYLES } from "./DayTimeline/ExceptionalStatusMarkers";

const EXCEPTIONAL_STATUS_LEGEND = [
  { status: "missing", translationKey: "exceptionalStatusMissing" },
  { status: "ignorable", translationKey: "exceptionalStatusIgnorable" },
  { status: "bad_labels", translationKey: "exceptionalStatusBadLabels" },
] as const;

const LegendSwatch = ({
  color,
  label,
  shape = "dot",
  opacity = 1,
}: {
  color: string;
  label: string;
  shape?: "dot" | "bar";
  opacity?: number;
}) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Box
      sx={{
        width: 12,
        height: shape === "bar" ? 6 : 12,
        borderRadius: shape === "bar" ? 0 : "50%",
        bgcolor: color,
        opacity,
      }}
    />
    <Typography variant="caption">{label}</Typography>
  </Stack>
);

export const ManualReviewLegend = ({
  date,
  showAllCranes,
}: {
  date: string;
  showAllCranes: boolean;
}) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();

  const { data: siteReviewPeriods } = useGetSiteReviewPeriodsQuery({
    tenantName,
    date,
  });
  const hasOtherCranes = (siteReviewPeriods?.locations.length ?? 0) > 1;

  return (
    <Stack
      direction="row"
      spacing={2}
      rowGap={0.5}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
    >
      <LegendSwatch color="success.main" label={t("verified")} />
      <LegendSwatch color="info.main" label={t("assignedToYou")} />
      <LegendSwatch color="warning.main" label={t("assignedToOther")} />
      <LegendSwatch color="error.main" label={t("notAssigned")} />
      {EXCEPTIONAL_STATUS_LEGEND.map(({ status, translationKey }) => (
        <LegendSwatch
          key={status}
          color={EXCEPTIONAL_STATUS_STYLES[status].bgcolor}
          opacity={EXCEPTIONAL_STATUS_STYLES[status].opacity}
          label={t(translationKey)}
          shape="bar"
        />
      ))}
      <LegendSwatch color="data.green" label={t("craneLoading")} shape="bar" />
      {hasOtherCranes && showAllCranes ? (
        <Typography variant="caption" color="text.secondary">
          {t("clickTimelineToOpenCraneInNewTab")}
        </Typography>
      ) : null}
    </Stack>
  );
};
